import React, { useState, useEffect } from 'react';
import { FaPlay, FaShare, FaHeart, FaGift, FaUsers, FaShoppingCart, FaThumbsDown, FaRegBell, FaUserFriends } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { database, ref as dbRef, onValue, get, set } from '../firebase';

const Gift = () => {
  const [showGiftPopup, setShowGiftPopup] = useState(false);
  const [claimedGifts, setClaimedGifts] = useState([]);
  const [pointsData, setPointsData] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGift, setSelectedGift] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [userMobile, setUserMobile] = useState(null);
  const navigate = useNavigate();

  // Fetch user mobile from localStorage
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (!currentUser || !currentUser.mobile) {
      setError('Please log in to view your rewards.');
      setLoading(false);
      navigate('/login');
      return;
    }
    setUserMobile(currentUser.mobile);
  }, [navigate]);

  // Sanitize mobile number
  const sanitizedMobile = userMobile ? userMobile.replace(/[^0-9]/g, '_') : null;

  // Fetch points data, gifts, and total points from Firebase
  useEffect(() => {
    if (!sanitizedMobile) return;

    // Define userGiftsRef and userRef only if sanitizedMobile is available
    const userGiftsRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/gifts`);
    const userRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}`);

    // Fetch totalpoints for each type
    const pointTypes = [
      { type: 'shares', icon: <FaShare className="text-green-500" />, label: 'Shares Points' },
      { type: 'likes', icon: <FaHeart className="text-red-500" />, label: 'Likes Points' },
      { type: 'unlike', icon: <FaThumbsDown className="text-purple-500" />, label: 'Unlike Points' },
      { type: 'subscribe', icon: <FaRegBell className="text-pink-500" />, label: 'Subscribe Points' },
      // Invite points and purchase points will be added after fetching
    ];

    const fetchPointsData = async () => {
      const pointsArray = [];
      await Promise.all(pointTypes.map(async (pt, idx) => {
        const ref = dbRef(database, `videoplayer/Users/${sanitizedMobile}/getextrapoints/${pt.type}/totalpoints`);
        try {
          const snap = await get(ref);
          const value = snap.exists() ? parseInt(snap.val(), 10) || 0 : 0;
          pointsArray.push({
            id: idx + 1,
            type: pt.type,
            icon: pt.icon,
            points: value,
            label: pt.label
          });
        } catch (err) {
          pointsArray.push({
            id: idx + 1,
            type: pt.type,
            icon: pt.icon,
            points: 0,
            label: pt.label
          });
        }
      }));

      // Fetch and sum invite points
      let invitePoints = 0;
      try {
        const inviteRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/invite`);
        const inviteSnap = await get(inviteRef);
        if (inviteSnap.exists()) {
          const inviteData = inviteSnap.val();
          invitePoints = Object.values(inviteData)
            .map(batch => parseInt(batch.points, 10) || 0)
            .reduce((a, b) => a + b, 0);
        }
      } catch {}
      pointsArray.push({
        id: pointTypes.length + 1,
        type: 'invite',
        icon: <FaUserFriends className="text-yellow-500" />,
        points: invitePoints,
        label: 'Invite Points'
      });

      // Fetch purchasePoints from user node
      let purchasePoints = 0;
      try {
        const purchaseRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/purchasePoints`);
        const purchaseSnap = await get(purchaseRef);
        purchasePoints = purchaseSnap.exists() ? parseInt(purchaseSnap.val(), 10) || 0 : 0;
      } catch {}
      pointsArray.push({
        id: pointTypes.length + 2,
        type: 'purchase',
        icon: <FaShoppingCart className="text-orange-500" />,
        points: purchasePoints,
        label: 'Purchase Points'
      });

      setPointsData(pointsArray);
    };
    fetchPointsData();

    // Fetch user gifts
    const unsubscribeGifts = onValue(
      userGiftsRef,
      (snapshot) => {
        const giftsData = snapshot.val();
        if (giftsData) {
          const giftsArray = Object.entries(giftsData)
            .map(([id, gift]) => ({
              giftId: parseInt(id),
              giftName: gift.giftName,
              icon: '🎁',
              assignedAt: gift.assignedAt,
              updatedAt: gift.updatedAt || null,
              status: gift.status || '',
            }))
            .sort((a, b) => b.giftId - a.giftId);
          setGifts(giftsArray);
        } else {
          setGifts([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching user gifts:', error);
        setError('Failed to load user gifts. Please try again later.');
        setLoading(false);
      }
    );

    // Fetch total points
    const unsubscribeUserPoints = onValue(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setTotalPoints(snapshot.val().points || 0);
        } else {
          setTotalPoints(0);
          setError('User data not found. Please log in again.');
          navigate('/login');
        }
      },
      (error) => {
        console.error('Error fetching user points:', error);
        setError('Failed to load user points. Please try again later.');
      }
    );

    return () => {
      unsubscribeGifts();
      unsubscribeUserPoints();
    };
  }, [sanitizedMobile, navigate]);

  // Remove points-based claiming logic
  const handleClaimGift = () => {
    // Just pick the next unclaimed, unhidden gift
    const claimableGifts = gifts.filter((gift) => !claimedGifts.includes(gift.giftId) && gift.status !== 'hide');
    const nextGift = claimableGifts.length > 0 ? claimableGifts[0] : null;
    setSelectedGift(nextGift);
    setShowGiftPopup(true);
  };

  const handleCollectGift = async () => {
    if (!selectedGift || !sanitizedMobile) return;
    const userGiftRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/gifts/${selectedGift.giftId}`);
    try {
      // Set the gift status to 'hide' so it can't be claimed again
      await set(userGiftRef, { ...selectedGift, status: 'hide' });
      setClaimedGifts([...claimedGifts, selectedGift.giftId]);
      setShowGiftPopup(false);
      setSelectedGift(null);
    } catch (err) {
      console.error('Error collecting gift:', err);
      setError('Failed to collect gift. Please try again.');
      setShowGiftPopup(false);
      setSelectedGift(null);
    }
  };

  // Remove points-based UI in the gift box and popup
  // Determine the next target (100, 200, 300, etc.)
  const getNextTarget = (points) => {
    return Math.ceil(points / 100) * 100 || 100; // Start at 100 and increment by 100
  };

  const nextGiftThreshold = getNextTarget(totalPoints);
  const progressPercentage = Math.min((totalPoints / nextGiftThreshold) * 100, 100);

  const nextGift =
    gifts.length > 0 ? gifts.filter((g) => !claimedGifts.includes(g.giftId) && g.status !== 'hide').sort((a, b) => a.points - b.points)[0] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-center text-indigo-800 mb-8">Your Rewards Dashboard</h1>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-600">{error}</div>
        ) : (
          <>
            {/* Points Cards Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-10">
              {pointsData.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="p-6 flex items-center">
                    <div className="mr-4 text-3xl">{item.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{item.label}</h3>
                      <p className="text-2xl font-bold text-indigo-600">{item.points}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Gift Box Section */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-10">
              <div className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <FaGift className="text-6xl text-yellow-500 animate-bounce" style={{ animationDuration: '2s' }} />
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                      {gifts.filter((g) => !claimedGifts.includes(g.giftId) && g.status !== 'hide').length}
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-2">You have {totalPoints} points!</h2>
                <p className="text-gray-600 mb-6">
                  {claimedGifts.length > 0 ? `You've claimed ${claimedGifts.length} gifts so far!` : 'Earn points to unlock amazing rewards'}
                </p>

                <button
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 px-8 rounded-full hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleClaimGift}
                  disabled={gifts.filter((g) => !claimedGifts.includes(g.giftId) && g.status !== 'hide').length === 0}
                >
                  {claimedGifts.length > 0 ? 'Claim Another Extra Commission' : 'Claim Your Extra Commission'}
                </button>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">How to earn more points?</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    {/* Removed Watch videos */}
                    <li className="flex items-center justify-center">
                      <FaShare className="mr-2 text-green-400" /> Shares content
                    </li>
                    <li className="flex items-center justify-center">
                      <FaHeart className="mr-2 text-red-400" /> Likes posts
                    </li>
                    <li className="flex items-center justify-center">
                      <FaThumbsDown className="mr-2 text-purple-400" /> Unlike content
                    </li>
                    <li className="flex items-center justify-center">
                      <FaUsers className="mr-2 text-yellow-400" /> Invite friends
                    </li>
                    <li className="flex items-center justify-center">
                      <FaShoppingCart className="mr-2 text-orange-400" /> Make purchases
                    </li>
                    <li className="flex items-center justify-center">
                      <FaRegBell className="mr-2 text-pink-400" /> Subscribe
                    </li>
                    <li className="flex items-center justify-center">
                      <FaGift className="mr-2 text-indigo-400" /> Daily check-ins
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Progress</h3>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 h-4 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-gray-600">{totalPoints} points earned</p>
                <p className="text-sm text-gray-600">
                  {totalPoints >= nextGiftThreshold ? 'Target reached!' : `${nextGiftThreshold - totalPoints} to next target (${nextGiftThreshold} points)`}
                </p>
              </div>

              {/* Claimed Gifts Table */}
              {claimedGifts.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Claimed Gifts</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 font-semibold text-gray-800">Gift Name</th>
                          <th className="p-3 font-semibold text-gray-800">Icon</th>
                        </tr>
                      </thead>
                      <tbody>
                        {claimedGifts.map((giftId) => {
                          const gift = gifts.find((g) => g.giftId === giftId && g.status !== 'hide');
                          if (!gift) return null;
                          return (
                            <tr key={giftId} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="p-3">{gift.giftName}</td>
                              <td className="p-3 text-2xl">{gift.icon}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Gift Popup */}
      {showGiftPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => {
                setShowGiftPopup(false);
                setSelectedGift(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <MdClose size={24} />
            </button>

            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedGift ? 'Congratulations!' : 'Keep Earning!'}</h2>
              {selectedGift ? (
                <>
                  <p className="text-gray-600 mb-4">You've unlocked:</p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center">
                      <span className="text-3xl mr-3">{selectedGift.icon}</span>
                      <div>
                        <h3 className="font-bold text-lg">{selectedGift.giftName}</h3>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCollectGift}
                    className="bg-indigo-600 text-white font-bold py-2 px-6 rounded-full hover:bg-indigo-700 transition-colors duration-300 mb-4"
                  >
                    Collect Gift
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    No more gifts to claim.
                  </p>
                </>
              )}
              <button
                onClick={() => {
                  setShowGiftPopup(false);
                  setSelectedGift(null);
                }}
                className="bg-gray-300 text-gray-700 font-bold py-2 px-6 rounded-full hover:bg-gray-400 transition-colors duration-300 mt-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gift;